/**
 * PostGrid Control Center — Platform Settings → Integrations → PostGrid
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../context/ThemeContext';
import { usePlatformIntegrationAuth } from '../../../../hooks/usePlatformIntegrationAuth';
import {
  IntegrationLoadingSpinner,
  IntegrationSetupNotice,
  integrationT,
  isIntegrationFetchFailure,
} from '../../../../components/platform/IntegrationFetchState';
import {
  ArrowLeft,
  Mail,
  RefreshCw,
  Copy,
  Loader2,
  Zap,
  Key,
  Eye,
  EyeOff,
  Webhook,
  Cog,
  BarChart3,
  Gauge,
  Shield,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { toast } from 'sonner';
import './PlatformPostGridSettings.css';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '').replace(/\/api$/, '');
const POSTGRID_SETTINGS_URL = `${API_BASE}/api/platform/settings/integrations/postgrid`;

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'api-settings', label: 'API Settings', icon: Key },
  { id: 'webhooks', label: 'Webhooks', icon: Webhook },
  { id: 'automations', label: 'Automations', icon: Cog },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'usage-metrics', label: 'Usage Metrics', icon: Gauge },
];

const SENDER_POLICY_OPTIONS = [
  { value: 'user_client_sender', label: 'User/Client Sender Address, recommended' },
  { value: 'agency_sender', label: 'Agency Sender Address, agency/system mail only' },
  { value: 'platform_default_sender', label: 'Platform Default Sender, fallback/test only' },
  { value: 'custom_sender_profile', label: 'Custom Sender Profile, approved workflows only' },
];

const PER_MAIL_FIELDS = ['full_name', 'address_line1', 'city', 'state', 'postal_code', 'country'];

const WEBHOOK_EVENTS = [
  { event: 'letter.created', desc: 'Creates or updates internal mail job record.' },
  { event: 'letter.submitted', desc: 'Moves letter into submitted status.' },
  { event: 'letter.printing', desc: 'Marks mail as printing with provider.' },
  { event: 'letter.processed', desc: 'Settles pending credits when provider accepts.' },
  { event: 'letter.completed', desc: 'Marks delivery lifecycle complete.' },
  { event: 'letter.cancelled', desc: 'Releases credits when cancellation is confirmed.' },
  { event: 'letter.failed', desc: 'Shows failure and unlocks retry workflow.' },
  { event: 'return_mail', desc: 'Flags returned or undeliverable mail for review.' },
];

const AUTOMATION_RULES = [
  { trigger: 'letter.processed', action: 'Mark processed for delivery', credit: 'Settle pending credits', notification: 'Optional', status: 'active' },
  { trigger: 'letter.cancelled', action: 'Keep history and close action', credit: 'Release or refund credits', notification: 'Send cancellation notice', status: 'active' },
  { trigger: 'letter.failed', action: 'Create retry task', credit: 'Hold until resolved', notification: 'Notify support', status: 'review' },
];

const MAIL_CLASS_ROWS = [
  { key: 'standard', label: 'Standard Class' },
  { key: 'certified', label: 'Certified Mail' },
  { key: 'certified_electronic_return_receipt', label: 'Certified + ERR' },
];

const EMPTY_FALLBACK = {
  name: '',
  company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
};

function policyLabel(policy) {
  const found = SENDER_POLICY_OPTIONS.find((o) => o.value === policy);
  return found ? found.label.split(',')[0] : 'User/Client';
}

async function apiFetch(url, { token, method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  return { ok: res.ok, status: res.status, data };
}

function Pill({ children, tone = 'default' }) {
  return <span className={`pg-pill ${tone !== 'default' ? tone : ''}`}>{children}</span>;
}

function formatMoney(n) {
  if (n == null || Number.isNaN(n)) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

function formatPct(num, den) {
  if (!den) return 'N/A';
  return `${Math.round((num / den) * 100)}%`;
}

function normalizeEnvironment(value) {
  const mode = String(value || '').toLowerCase();
  if (mode === 'test') return 'sandbox';
  if (mode === 'live') return 'live';
  if (mode === 'sandbox') return 'sandbox';
  return '';
}

export default function PlatformPostGridSettings() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { token, authLoading, canFetch, requestConfig } = usePlatformIntegrationAuth();
  const isDark = resolvedTheme === 'dark';

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const fetchWarned = useRef(false);
  const apiSettingsFormRef = useRef(null);

  const [postgridStatus, setPostgridStatus] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [bureauAddresses, setBureauAddresses] = useState({});
  const [senderPolicy, setSenderPolicy] = useState('user_client_sender');
  const [fallbackSender, setFallbackSender] = useState(EMPTY_FALLBACK);
  const [senderPanel, setSenderPanel] = useState('user_client_sender');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('sandbox');
  const [apiSaved, setApiSaved] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookEventsTotal, setWebhookEventsTotal] = useState(0);
  const [webhookEventsLoading, setWebhookEventsLoading] = useState(false);
  const [webhookSecretInput, setWebhookSecretInput] = useState('');

  const [letters, setLetters] = useState([]);
  const [lettersTotal, setLettersTotal] = useState(0);
  const [lettersPage, setLettersPage] = useState(1);
  const [lettersTotalPages, setLettersTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [activityLoading, setActivityLoading] = useState(false);
  const [lettersUnavailable, setLettersUnavailable] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    dateRange: '30',
    status: 'all',
    type: 'all',
    environment: 'all',
  });

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : requestConfig().headers;

  const loadPostgridStatus = useCallback(async () => {
    const { ok, data } = await apiFetch(`${API_BASE}/api/mailing-config/postgrid-status`, { token });
    if (ok && data) {
      setPostgridStatus(data);
      if (data.sender_resolution_policy) setSenderPolicy(data.sender_resolution_policy);
      return true;
    }
    setPostgridStatus({ configured: false, mode: 'none', api_key_present: false });
    return false;
  }, [token]);

  const loadPlatformSettings = useCallback(async () => {
    const { ok, data } = await apiFetch(POSTGRID_SETTINGS_URL, { token });
    if (ok && data) {
      setPlatformSettings(data);
      if (data.sender_resolution_policy) {
        setSenderPolicy(data.sender_resolution_policy);
        setSenderPanel(data.sender_resolution_policy);
      }
      if (data.platform_fallback_sender) {
        setFallbackSender({ ...EMPTY_FALLBACK, ...data.platform_fallback_sender });
      }
      return true;
    }
    return false;
  }, [token]);

  const loadBureaus = useCallback(async () => {
    const { ok, data } = await apiFetch(`${API_BASE}/api/mailing-config/bureau-addresses`, { token });
    if (ok && data?.bureaus) setBureauAddresses(data.bureaus);
  }, [token]);

  const loadWebhookEvents = useCallback(async () => {
    setWebhookEventsLoading(true);
    const { ok, data } = await apiFetch(`${POSTGRID_SETTINGS_URL}/webhook-events?limit=50`, { token });
    if (ok && data) {
      setWebhookEvents(data.items || []);
      setWebhookEventsTotal(data.total ?? (data.items || []).length);
    } else {
      setWebhookEvents([]);
      setWebhookEventsTotal(0);
    }
    setWebhookEventsLoading(false);
  }, [token]);

  const loadLetters = useCallback(async () => {
    setActivityLoading(true);
    const limit = rowsPerPage === 'all' ? 500 : rowsPerPage;
    const params = new URLSearchParams({
      limit: String(limit),
      page: String(lettersPage),
    });
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.environment !== 'all') params.set('environment', filters.environment);
    if (filters.search.trim()) params.set('search', filters.search.trim());

    const { ok, status, data } = await apiFetch(
      `${API_BASE}/api/admin/mailed-letters?${params}`,
      { token }
    );
    if (ok && data) {
      setLetters(data.letters || []);
      setLettersTotal(data.total ?? 0);
      setLettersTotalPages(data.total_pages ?? 1);
      setLettersUnavailable(false);
    } else if (status === 404 || status === 403) {
      setLetters([]);
      setLettersTotal(0);
      setLettersUnavailable(true);
    } else {
      setLetters([]);
    }
    setActivityLoading(false);
  }, [token, lettersPage, rowsPerPage, filters.status, filters.environment, filters.search]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setFetchFailed(false);
    const results = await Promise.all([loadPostgridStatus(), loadPlatformSettings(), loadBureaus()]);
    if (results.every((r) => r === false)) {
      setFetchFailed(true);
      if (!fetchWarned.current) {
        fetchWarned.current = true;
      }
    }
    setLoading(false);
    await loadLetters();
  }, [loadPostgridStatus, loadPlatformSettings, loadBureaus, loadLetters]);

  useEffect(() => {
    if (!canFetch) return;
    loadAll();
  }, [canFetch, loadAll]);

  useEffect(() => {
    if (!canFetch || loading) return;
    loadLetters();
  }, [canFetch, loading, loadLetters]);

  useEffect(() => {
    const inferred =
      normalizeEnvironment(platformSettings?.environment) ||
      normalizeEnvironment(postgridStatus?.mode) ||
      'sandbox';
    setSelectedEnvironment(inferred);
  }, [platformSettings?.environment, postgridStatus?.mode]);

  useEffect(() => {
    if (activeTab !== 'api-settings') return;
    if (!apiSettingsFormRef.current) return;
    apiSettingsFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeTab]);

  useEffect(() => {
    if (!canFetch || activeTab !== 'webhooks') return;
    loadWebhookEvents();
  }, [canFetch, activeTab, loadWebhookEvents]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { ok, data } = await apiFetch(`${POSTGRID_SETTINGS_URL}/refresh-status`, {
        token,
        method: 'POST',
      });
      if (ok && data?.success) {
        const updated = data.updated ?? 0;
        toast.success(data.message || `Status refreshed (${updated} updated)`);
      } else if (!ok) {
        toast.error(data?.detail || data?.error || 'Status refresh failed');
      }
    } catch (err) {
      toast.error(err?.message || 'Status refresh failed');
    }
    await loadAll();
    if (activeTab === 'webhooks') {
      await loadWebhookEvents();
    }
    setRefreshing(false);
  };

  const openApiSettingsForm = () => {
    setActiveTab('api-settings');
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { ok, data } = await apiFetch(`${POSTGRID_SETTINGS_URL}/test`, { token, method: 'POST' });
      if (ok && data) {
        setValidationResult(data);
        if (data.success) toast.success(data.message || 'Connection test passed');
        else toast.error(data.message || data.detail || 'Connection test failed');
      } else {
        setValidationResult({
          success: false,
          connection_status: 'Network Error',
          message: data?.detail || data?.message || 'Connection test failed',
          last_validation: new Date().toISOString(),
        });
        toast.error(data?.detail || data?.message || 'Connection test failed');
      }
      await loadPlatformSettings();
      await loadPostgridStatus();
    } catch {
      setValidationResult({
        success: false,
        connection_status: 'Network Error',
        message: 'Connection test failed',
        last_validation: new Date().toISOString(),
      });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const saveApiKeySettings = async () => {
    const hasExistingKey = Boolean(
      platformSettings?.api_key_configured || postgridStatus?.api_key_present
    );
    if (!apiKeyInput.trim() && !hasExistingKey) {
      toast.error('Enter a PostGrid API key before saving.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        environment: selectedEnvironment || 'sandbox',
        enabled: true,
      };
      if (apiKeyInput.trim()) payload.api_key = apiKeyInput.trim();
      const { ok, data } = await apiFetch(POSTGRID_SETTINGS_URL, {
        token,
        method: 'PUT',
        body: payload,
      });
      if (!ok) {
        toast.error(data?.detail || data?.message || 'Failed to save API key');
        return;
      }
      setApiSaved(true);
      setApiKeyInput('');
      setShowApiKey(false);
      setValidationResult(null);
      toast.success('PostGrid API settings saved');
      await loadPlatformSettings();
      await loadPostgridStatus();
    } finally {
      setSaving(false);
    }
  };

  const saveSenderPolicy = async () => {
    setSaving(true);
    const { ok, data } = await apiFetch(`${API_BASE}/api/mailing-config/sender-resolution-policy`, {
      token,
      method: 'PUT',
      body: { sender_resolution_policy: senderPolicy },
    });
    if (ok) {
      await apiFetch(POSTGRID_SETTINGS_URL, {
        token,
        method: 'PUT',
        body: { sender_resolution_policy: senderPolicy },
      });
      toast.success('Sender resolution rules saved');
      await loadPostgridStatus();
      await loadPlatformSettings();
    } else {
      toast.error(data?.detail || 'Failed to save sender policy');
    }
    setSaving(false);
  };

  const saveFallbackSender = async () => {
    setSaving(true);
    const { ok, data } = await apiFetch(`${API_BASE}/api/mailing-config/platform-fallback-sender`, {
      token,
      method: 'PUT',
      body: { address: fallbackSender },
    });
    if (ok) {
      toast.success('Platform fallback sender saved');
      await loadPlatformSettings();
    } else {
      toast.error(data?.detail || 'Failed to save fallback sender');
    }
    setSaving(false);
  };

  const copyWebhookUrl = () => {
    const url = platformSettings?.webhook_url || `${API_BASE}/api/webhooks/postgrid`;
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied');
  };

  const saveWebhookSecret = async () => {
    if (!webhookSecretInput.trim()) {
      toast.error('Enter the PostGrid webhook secret from the PostGrid dashboard.');
      return;
    }
    setSaving(true);
    try {
      const { ok, data } = await apiFetch(POSTGRID_SETTINGS_URL, {
        token,
        method: 'PUT',
        body: { webhook_secret: webhookSecretInput.trim() },
      });
      if (!ok) {
        toast.error(data?.detail || data?.message || 'Failed to save webhook secret');
        return;
      }
      setWebhookSecretInput('');
      toast.success('Webhook secret saved');
      await loadPlatformSettings();
      await loadPostgridStatus();
    } finally {
      setSaving(false);
    }
  };

  const metrics = useMemo(() => {
    const counts = {
      total: lettersTotal || letters.length,
      ready: 0,
      submitted: 0,
      printing: 0,
      processed: 0,
      completed: 0,
      failed: 0,
      cancelled: 0,
      released: 0,
    };
    let totalCost = 0;
    let pendingCredits = 0;
    let usedCredits = 0;
    let refundedCredits = 0;
    let certified = 0;
    let errMail = 0;

    letters.forEach((item) => {
      const status = String(item.status || '').toLowerCase();
      if (counts[status] !== undefined) counts[status]++;
      totalCost += parseFloat(item.cost) || 0;
      const cs = String(item.credit_status || '').toLowerCase();
      if (cs === 'pending') pendingCredits += 1;
      if (cs === 'used') usedCredits += 1;
      if (cs === 'released' || cs === 'refunded') refundedCredits += 1;
      const mc = String(item.mail_class || '').toLowerCase();
      if (mc.includes('certified')) certified += 1;
      if (mc.includes('err') || mc.includes('return')) errMail += 1;
    });

    return { ...counts, totalCost, pendingCredits, usedCredits, refundedCredits, certified, errMail };
  }, [letters, lettersTotal]);

  const recentDeliveries24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return letters.filter((l) => {
      const s = String(l.status || '').toLowerCase();
      if (!['submitted', 'completed', 'processed'].includes(s)) return false;
      const t = l.created_at ? new Date(l.created_at).getTime() : 0;
      return t >= cutoff;
    }).length;
  }, [letters]);

  const analytics = useMemo(() => {
    const completed = metrics.completed;
    const failed = metrics.failed;
    const denom = completed + failed;
    const volumeByDay = {};
    letters.forEach((l) => {
      if (!l.created_at) return;
      const d = new Date(l.created_at);
      const key = d.toLocaleDateString('en-US', { weekday: 'short' });
      volumeByDay[key] = (volumeByDay[key] || 0) + 1;
    });
    const agencySpend = {};
    letters.forEach((l) => {
      const name = l.agency_name || 'Unknown';
      agencySpend[name] = (agencySpend[name] || 0) + (parseFloat(l.cost) || 0);
    });
    const topAgencies = Object.entries(agencySpend)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const maxAgency = topAgencies[0]?.[1] || 0;
    return {
      deliveryRate: formatPct(completed, denom),
      avgCost: metrics.total > 0 ? formatMoney(metrics.totalCost / metrics.total) : 'N/A',
      failureRate: formatPct(failed, denom),
      volumeByDay,
      topAgencies,
      maxAgency,
    };
  }, [letters, metrics]);

  const usageByClass = useMemo(() => {
    const map = {};
    MAIL_CLASS_ROWS.forEach((r) => {
      map[r.key] = { volume: 0, cost: 0, credits: 0 };
    });
    letters.forEach((l) => {
      const key = String(l.mail_class || 'standard').toLowerCase();
      const bucket = map[key] || map.standard;
      bucket.volume += 1;
      bucket.cost += parseFloat(l.cost) || 0;
      if (String(l.credit_status).toLowerCase() === 'used') bucket.credits += 1;
    });
    return MAIL_CLASS_ROWS.map((r) => ({
      ...r,
      ...(map[r.key] || { volume: 0, cost: 0, credits: 0 }),
    }));
  }, [letters]);

  const usageByAgency = useMemo(() => {
    const map = {};
    letters.forEach((l) => {
      const name = l.agency_name;
      if (!name) return;
      if (!map[name]) map[name] = { volume: 0, cost: 0 };
      map[name].volume += 1;
      map[name].cost += parseFloat(l.cost) || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].cost - a[1].cost);
  }, [letters]);

  const filteredLetters = useMemo(() => {
    let rows = [...letters];
    if (filters.type !== 'all') {
      rows = rows.filter((r) => String(r.type || r.bureau || '').toLowerCase().includes(filters.type));
    }
    if (filters.dateRange !== 'all') {
      const days = parseInt(filters.dateRange, 10) || 30;
      const cutoff = Date.now() - days * 86400000;
      rows = rows.filter((r) => {
        const t = r.created_at ? new Date(r.created_at).getTime() : 0;
        return t >= cutoff;
      });
    }
    return rows;
  }, [letters, filters.type, filters.dateRange]);

  const pageStart = lettersTotal === 0 ? 0 : (lettersPage - 1) * (rowsPerPage === 'all' ? lettersTotal : rowsPerPage) + 1;
  const pageEnd = Math.min(
    lettersPage * (rowsPerPage === 'all' ? lettersTotal : rowsPerPage),
    lettersTotal
  );

  const integrationReady =
    postgridStatus?.integration_sender_ready ??
    platformSettings?.integration_sender_ready ??
    false;
  const connected = postgridStatus?.configured || platformSettings?.api_key_configured;
  const webhookSecretOk =
    postgridStatus?.webhook_secret_present || platformSettings?.webhook_secret_configured;
  const automationsBackend = platformSettings?.automations;
  const automationsAvailable = automationsBackend && Object.values(automationsBackend).some((v) => v !== null);

  if (authLoading) {
    return <IntegrationLoadingSpinner message={integrationT('loadingAuth', 'Checking session…')} />;
  }

  if (!canFetch) {
    return (
      <IntegrationSetupNotice
        title={integrationT('signInTitle', 'Sign in required')}
        description={integrationT('signInPostGrid', 'Sign in as a platform administrator to manage PostGrid.')}
      />
    );
  }

  if (loading) {
    return <IntegrationLoadingSpinner message={integrationT('loadingPostGrid', 'Loading PostGrid Control Center…')} />;
  }

  const renderOverview = () => (
    <>
      <div className="pg-status-grid">
        <article className="pg-status-card">
          <div className="pg-label">Connection Status</div>
          {connected ? (
            <Pill tone="green"><span className="pg-dot" /> Connected</Pill>
          ) : (
            <Pill tone="red"><span className="pg-dot" /> Not connected</Pill>
          )}
          <p className="pg-subtitle" style={{ marginTop: 8, fontSize: '0.85rem' }}>
            {connected ? postgridStatus?.message : 'Add a PostGrid API key and run a connection test.'}
          </p>
        </article>
        <article className="pg-status-card">
          <div className="pg-label">Sender Resolution</div>
          <div className="pg-value" style={{ fontSize: '1.15rem' }}>{policyLabel(senderPolicy)}</div>
          <p className="pg-subtitle" style={{ marginTop: 8, fontSize: '0.85rem' }}>
            Dispute letters use each user/client profile address.
          </p>
        </article>
        <article className="pg-status-card">
          <div className="pg-label">Active Webhooks</div>
          <div className="pg-value">{webhookSecretOk ? 1 : 0}</div>
          <p className="pg-subtitle" style={{ marginTop: 8, fontSize: '0.85rem' }}>
            Configure webhook events in the Webhooks tab.
          </p>
        </article>
        <article className="pg-status-card">
          <div className="pg-label">Recent Deliveries</div>
          <div className="pg-value">{recentDeliveries24h}</div>
          <p className="pg-subtitle" style={{ marginTop: 8, fontSize: '0.85rem' }}>
            Submitted and completed mail in the last 24 hours (loaded page).
          </p>
        </article>
      </div>

      <section className="pg-section tinted">
        <h2 style={{ margin: '0 0 6px', fontSize: '1.25rem' }}>PostGrid Health</h2>
        <p className="pg-subtitle">Real-time integration health monitoring and setup readiness.</p>
        <div className="pg-health-grid" style={{ marginTop: 16 }}>
          <div className="pg-health-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>API Key</strong>
              {postgridStatus?.api_key_present ? (
                <Pill tone="green"><span className="pg-dot" /> Configured</Pill>
              ) : (
                <Pill tone="red"><span className="pg-dot" /> Not connected</Pill>
              )}
            </div>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              {postgridStatus?.api_key_present
                ? 'API key is present in environment or encrypted storage.'
                : 'No API key configured. Add a sandbox or live key before sending mail.'}
            </p>
            <Button type="button" size="sm" className="mt-3" onClick={openApiSettingsForm}>
              Configure API Key
            </Button>
          </div>
          <div className="pg-health-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>API Key Valid</strong>
              {platformSettings?.last_test_status === 'passed' ? (
                <Pill tone="green"><span className="pg-dot" /> Validated</Pill>
              ) : (
                <Pill tone="gold"><span className="pg-dot" /> Validation pending</Pill>
              )}
            </div>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              Run the connection test after saving keys.
            </p>
            <Button type="button" size="sm" variant="outline" className="mt-3" onClick={handleTestConnection} disabled={testing}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run Validation'}
            </Button>
          </div>
          <div className="pg-health-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Sender Resolution</strong>
              <Pill tone="green"><span className="pg-dot" /> User/Client Sender</Pill>
            </div>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              Dispute letters use the user/client profile address as the sender. Missing fields are checked per mail job, not as a global blocker.
            </p>
            <div className="pg-checklist">
              <span className="pg-check-item good">Required per mail job</span>
              {PER_MAIL_FIELDS.map((f) => (
                <span key={f} className="pg-check-item">{f}</span>
              ))}
            </div>
            <Button type="button" size="sm" className="mt-3" onClick={openApiSettingsForm}>
              Review Sender Rules
            </Button>
          </div>
        </div>
      </section>

      <section className="pg-section tinted">
        <h2 style={{ margin: '0 0 6px', fontSize: '1.25rem' }}>Mail Activity Overview</h2>
        <div className="pg-metric-grid" style={{ marginTop: 16 }}>
          {[
            ['Total', metrics.total, 'All loaded records'],
            ['Ready', metrics.ready, 'Waiting for approval'],
            ['Submitted', metrics.submitted, 'Waiting for PostGrid'],
            ['Printing', metrics.printing, 'Print jobs'],
            ['Processed', metrics.processed, 'Processed mail'],
            ['Completed', metrics.completed, 'Completed mail'],
            ['Failed', metrics.failed, 'Failures'],
            ['Cancelled', metrics.cancelled, 'Cancellations'],
            ['PostGrid Cost', formatMoney(metrics.totalCost), 'Provider spend'],
            ['Pending Credits', metrics.pendingCredits, 'Credits pending'],
            ['Credits Used', metrics.usedCredits, 'Credits used'],
            ['Released', metrics.released, 'Credits released'],
          ].map(([title, val, help]) => (
            <div key={title} className="pg-metric">
              <div className="pg-label">{title}</div>
              <div className="pg-value" style={{ fontSize: '1.25rem' }}>{val}</div>
              <p className="pg-subtitle" style={{ fontSize: '0.75rem', marginTop: 4 }}>{help}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pg-section">
        <div className="pg-filters">
          <div className="pg-field">
            <label>Search</label>
            <input
              placeholder="PostGrid ID, recipient, user, agency…"
              value={filters.search}
              onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            />
          </div>
          <div className="pg-field">
            <label>Date Range</label>
            <select value={filters.dateRange} onChange={(e) => setFilters((p) => ({ ...p, dateRange: e.target.value }))}>
              <option value="7">7 Days</option>
              <option value="30">30 Days</option>
              <option value="90">90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div className="pg-field">
            <label>Status</label>
            <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="submitted">Submitted</option>
              <option value="printing">Printing</option>
              <option value="processed">Processed</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="pg-field">
            <label>Type</label>
            <select value={filters.type} onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}>
              <option value="all">All Types</option>
              <option value="dispute">Dispute Letter</option>
              <option value="test">Test Mail</option>
              <option value="collection">Proof / Collection</option>
            </select>
          </div>
          <div className="pg-field">
            <label>Environment</label>
            <select value={filters.environment} onChange={(e) => setFilters((p) => ({ ...p, environment: e.target.value }))}>
              <option value="all">All Env</option>
              <option value="sandbox">Sandbox</option>
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </div>
          <Button type="button" size="sm" onClick={() => { setLettersPage(1); loadLetters(); }}>Apply</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setFilters({ search: '', dateRange: '30', status: 'all', type: 'all', environment: 'all' })}>Reset</Button>
        </div>
      </section>

      <section className="pg-section">
        <h3 style={{ margin: '0 0 12px' }}>Recent Mail Activity</h3>
        {lettersUnavailable && (
          <div className="pg-notice warning" style={{ marginBottom: 12 }}>
            Mail activity API unavailable. Showing empty state until letters are recorded.
          </div>
        )}
        {activityLoading ? (
          <IntegrationLoadingSpinner message="Loading mail activity…" />
        ) : filteredLetters.length === 0 ? (
          <div className="pg-empty">
            <Mail className="mx-auto mb-3 opacity-40" size={40} />
            <h3>No mail activity yet</h3>
            <p className="pg-subtitle">
              Mail activity appears after letters are submitted to PostGrid. Keep sender resolution on user/client sender, then submit a test or dispute letter.
            </p>
            <Button type="button" className="mt-4" onClick={openApiSettingsForm}>Review API Settings</Button>
          </div>
        ) : (
          <div className="pg-table-wrap">
            <table className="pg-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Recipient</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>User</th>
                  <th>Agency</th>
                  <th>Mail Class</th>
                  <th>Cost</th>
                  <th>PostGrid ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredLetters.map((row) => (
                  <tr key={row.id || row.postgrid_id}>
                    <td>{row.type || row.bureau || '—'}</td>
                    <td>{row.recipient_name || '—'}</td>
                    <td>{row.status || '—'}</td>
                    <td>{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                    <td>{row.user_email || row.user_id || '—'}</td>
                    <td>{row.agency_name || '—'}</td>
                    <td>{row.mail_class || '—'}</td>
                    <td>{formatMoney(row.cost)}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.postgrid_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pg-pagination">
          <div>
            <div>Page {lettersPage} of {lettersTotalPages}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--pg-muted-2)' }}>
              Showing {pageStart}-{pageEnd} of {lettersTotal} mail records
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem' }}>
              Rows per page:{' '}
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  const v = e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10);
                  setRowsPerPage(v);
                  setLettersPage(1);
                }}
              >
                {[25, 50, 100, 250].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
            <Button type="button" size="sm" variant="outline" disabled={lettersPage <= 1} onClick={() => setLettersPage((p) => Math.max(1, p - 1))}>Prev</Button>
            <Button type="button" size="sm" variant="outline" disabled={lettersPage >= lettersTotalPages} onClick={() => setLettersPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </section>
    </>
  );

  const renderApiSettings = () => (
    <>
      <section className="pg-section tinted" ref={apiSettingsFormRef}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>API Settings</h2>
            <p className="pg-subtitle">Configure API keys, environment, sender resolution, mail classes, and bureau addresses.</p>
          </div>
          {connected ? <Pill tone="green"><span className="pg-dot" /> Configured</Pill> : <Pill tone="red"><span className="pg-dot" /> Not configured</Pill>}
        </div>
        <div className="pg-grid-2" style={{ marginTop: 16 }}>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 10px' }}>PostGrid API Key</h3>
            <div className="pg-field">
              <label>PostGrid API Key</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type={showApiKey ? 'text' : 'password'}
                  placeholder="Enter PostGrid API key"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowApiKey((v) => !v)}
                  aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                >
                  {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </Button>
              </div>
            </div>
            <div className="pg-field" style={{ marginTop: 10 }}>
              <label>Environment</label>
              <select
                value={selectedEnvironment || 'sandbox'}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
              >
                <option value="sandbox">Sandbox</option>
                <option value="live">Live</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Button type="button" onClick={saveApiKeySettings} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save API Key'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={testing || (!apiSaved && !platformSettings?.api_key_configured)}
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run Validation'}
              </Button>
            </div>
          </div>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 10px' }}>Status</h3>
            <div className="pg-checklist" style={{ marginTop: 0 }}>
              <span className="pg-check-item good">
                Active API Key: {(platformSettings?.api_key_configured || postgridStatus?.api_key_present) ? 'Configured' : 'Not configured'}
              </span>
              <span className={`pg-check-item ${
                validationResult?.connection_status === 'Connected' || platformSettings?.last_test_status === 'connected' || platformSettings?.last_test_status === 'passed'
                  ? 'good'
                  : ''
              }`}>
                API Key Valid: {
                  validationResult?.connection_status ||
                  (platformSettings?.last_test_status === 'connected' || platformSettings?.last_test_status === 'passed'
                    ? 'Valid'
                    : platformSettings?.last_test_status === 'invalid_key'
                      ? 'Invalid'
                      : 'Validation pending')
                }
              </span>
              <span className="pg-check-item">
                Environment: {selectedEnvironment ? (selectedEnvironment === 'live' ? 'Live' : 'Sandbox') : 'Not selected'}
              </span>
            </div>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem', marginTop: 10 }}>
              Saved key: {platformSettings?.api_key_masked || (postgridStatus?.api_key_present ? '••••' : 'Not configured')}
            </p>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem', marginTop: 8 }}>
              Connection Status:{' '}
              <strong>
                {validationResult?.connection_status ||
                  (platformSettings?.last_test_status === 'connected' || platformSettings?.last_test_status === 'passed'
                    ? 'Connected'
                    : platformSettings?.last_test_status === 'invalid_key'
                      ? 'Invalid Key'
                      : platformSettings?.last_test_status === 'rate_limited'
                        ? 'Rate Limited'
                        : platformSettings?.last_test_status === 'network_error'
                          ? 'Network Error'
                          : 'Validation pending')}
              </strong>
            </p>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem', marginTop: 4 }}>
              Last Validation:{' '}
              {validationResult?.last_validation
                ? new Date(validationResult.last_validation).toLocaleString()
                : platformSettings?.last_test_at
                  ? new Date(platformSettings.last_test_at).toLocaleString()
                  : 'Not run'}
            </p>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem', marginTop: 4 }}>
              Account Information:{' '}
              {(validationResult?.account_id || platformSettings?.last_account_id)
                ? `PostGrid Account ID ${validationResult?.account_id || platformSettings?.last_account_id}`
                : 'PostGrid Account ID unavailable'}
              {' · '}
              {(validationResult?.environment || platformSettings?.environment || selectedEnvironment || 'Not selected') === 'live' ? 'Live' : 'Sandbox'}
            </p>
          </div>
        </div>
        <div className="pg-grid-3" style={{ marginTop: 16 }}>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 8px' }}>Active API Key</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              {platformSettings?.api_key_masked || (postgridStatus?.api_key_present ? '••••••••' : 'No API key configured. Store keys in backend encrypted storage or environment variables.')}
            </p>
          </div>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 8px' }}>Pending Replacement Key</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>Stage a new key via platform settings API, test it, then promote after validation.</p>
          </div>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3 style={{ margin: '0 0 8px' }}>Environment</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              Mode: <strong>{postgridStatus?.mode === 'live' ? 'Live' : postgridStatus?.mode === 'test' ? 'Sandbox' : 'Not selected'}</strong>
            </p>
          </div>
        </div>
      </section>

      <section className="pg-section">
        <h2 style={{ margin: '0 0 8px' }}>Sender Resolution Rules</h2>
        <div className="pg-notice" style={{ marginBottom: 16 }}>
          <strong>Correct dispute letter behavior:</strong> The sender is resolved from the self-repair user profile or agency client/consumer profile at send time. Missing address fields block only that specific mail job.
        </div>
        <div className="pg-grid-2">
          <div className="pg-field">
            <label>Sender Resolution Policy</label>
            <select
              value={senderPolicy}
              onChange={(e) => {
                setSenderPolicy(e.target.value);
                setSenderPanel(e.target.value);
              }}
            >
              {SENDER_POLICY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className={`pg-sender-panel ${senderPanel === 'user_client_sender' ? 'active' : ''}`}>
          <div className="pg-card" style={{ padding: 16, marginTop: 12 }}>
            <h3>User/Client Sender Address</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              PostGrid uses the consumer address from the user or agency client profile. Missing fields block only that letter.
            </p>
            <div className="pg-checklist">
              <span className="pg-check-item good">Required per letter</span>
              {PER_MAIL_FIELDS.map((f) => (
                <span key={f} className="pg-check-item">{f}</span>
              ))}
            </div>
          </div>
        </div>

        <div className={`pg-sender-panel ${senderPanel === 'agency_sender' ? 'active' : ''}`}>
          <div className="pg-notice warning" style={{ marginTop: 12 }}>
            Only used for agency-branded/system mail. Consumer dispute letters should still use the client/consumer address unless explicitly overridden.
          </div>
        </div>

        <div className={`pg-sender-panel ${senderPanel === 'platform_default_sender' ? 'active' : ''}`}>
          <h3 style={{ marginTop: 16 }}>Optional Platform Fallback Sender</h3>
          <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
            Only used for platform test mail, admin/system mail, or fallback workflows. Not required for user/client dispute letters.
          </p>
          <div className="pg-grid-2" style={{ marginTop: 12 }}>
            {['name', 'company_name', 'address_line1', 'address_line2', 'city', 'state', 'postal_code'].map((field) => (
              <div key={field} className="pg-field">
                <label>{field.replace(/_/g, ' ')}</label>
                <input
                  value={fallbackSender[field] || ''}
                  onChange={(e) => setFallbackSender((p) => ({ ...p, [field]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <Button type="button" size="sm" className="mt-3" onClick={saveFallbackSender} disabled={saving}>Save Fallback Sender</Button>
        </div>

        <div className={`pg-sender-panel ${senderPanel === 'custom_sender_profile' ? 'active' : ''}`}>
          <div className="pg-notice warning" style={{ marginTop: 12 }}>
            Custom sender profiles are for approved workflows only and should not override consumer sender logic by default.
          </div>
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Button type="button" onClick={saveSenderPolicy} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Sender Resolution Rules'}
          </Button>
        </div>
      </section>

      <section className="pg-section">
        <h2>Mail Classes &amp; Bureau Addresses</h2>
        <div className="pg-grid-2" style={{ marginTop: 12 }}>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3>Supported Mail Classes</h3>
            <div className="pg-checklist">
              {MAIL_CLASS_ROWS.map((r) => (
                <span key={r.key} className="pg-check-item good">{r.label}</span>
              ))}
            </div>
          </div>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3>Bureau Addresses</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>Loaded from backend configuration.</p>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={loadBureaus}>Refresh Bureau Addresses</Button>
            {Object.keys(bureauAddresses).length > 0 && (
              <ul style={{ marginTop: 12, fontSize: '0.85rem', paddingLeft: 18 }}>
                {Object.keys(bureauAddresses).map((b) => (
                  <li key={b}>{bureauAddresses[b]?.name || b}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </>
  );

  const renderWebhooks = () => (
    <>
      <section className="pg-section tinted">
        <h2 style={{ margin: 0 }}>Webhooks</h2>
        <div className="pg-grid-2" style={{ marginTop: 16 }}>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3>Webhook URL</h3>
            <div className="pg-field" style={{ marginTop: 8 }}>
              <input readOnly value={platformSettings?.webhook_url || `${API_BASE}/api/webhooks/postgrid`} />
            </div>
            <Button type="button" size="sm" className="mt-2" onClick={copyWebhookUrl}><Copy size={14} className="mr-1" /> Copy URL</Button>
          </div>
          <div className="pg-card" style={{ padding: 16 }}>
            <h3>Webhook Secret</h3>
            <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>
              {webhookSecretOk
                ? `Configured (masked): ${platformSettings?.webhook_secret_masked || '••••'}`
                : 'Not configured — paste the secret from PostGrid Webhook Details.'}
            </p>
            <div className="pg-field" style={{ marginTop: 10 }}>
              <label>PostGrid Webhook Secret</label>
              <input
                type="password"
                placeholder="From PostGrid dashboard → Webhook Details"
                value={webhookSecretInput}
                onChange={(e) => setWebhookSecretInput(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <Button type="button" size="sm" className="mt-2" onClick={saveWebhookSecret} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Webhook Secret'}
            </Button>
          </div>
        </div>
      </section>
      <section className="pg-section">
        <h2>Supported Events</h2>
        <div className="pg-grid-4" style={{ marginTop: 12 }}>
          {WEBHOOK_EVENTS.map((ev) => (
            <div key={ev.event} className="pg-card" style={{ padding: 12 }}>
              <strong style={{ fontSize: '0.85rem' }}>{ev.event}</strong>
              <p className="pg-subtitle" style={{ fontSize: '0.75rem', marginTop: 4 }}>{ev.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="pg-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Recent Webhook Events</h2>
          <Button type="button" size="sm" variant="outline" onClick={loadWebhookEvents} disabled={webhookEventsLoading}>
            {webhookEventsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh Events'}
          </Button>
        </div>
        {webhookEventsLoading ? (
          <div className="pg-empty" style={{ marginTop: 12 }}>
            <Loader2 className="h-5 w-5 animate-spin" />
            <p className="pg-subtitle">Loading webhook events…</p>
          </div>
        ) : webhookEvents.length === 0 ? (
          <div className="pg-empty" style={{ marginTop: 12 }}>
            <p className="pg-subtitle">
              No webhook events yet. Events appear after PostGrid sends lifecycle updates to{' '}
              {platformSettings?.webhook_url || `${API_BASE}/api/webhooks/postgrid`}.
            </p>
          </div>
        ) : (
          <div className="pg-table-wrap" style={{ marginTop: 12 }}>
            <table className="pg-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Event</th>
                  <th>Status</th>
                  <th>PostGrid ID</th>
                  <th>Related Mail</th>
                </tr>
              </thead>
              <tbody>
                {webhookEvents.map((ev) => (
                  <tr key={ev.event_id || `${ev.postgrid_id}-${ev.received_at}`}>
                    <td>
                      {ev.received_at
                        ? new Date(ev.received_at).toLocaleString()
                        : '—'}
                    </td>
                    <td>{ev.event_type || '—'}</td>
                    <td>{ev.status || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ev.postgrid_id || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {ev.related_mail_id || ev.mailing_id || ev.send_id || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="pg-subtitle" style={{ marginTop: 8, fontSize: '0.8rem' }}>
              Showing {webhookEvents.length} of {webhookEventsTotal} events (newest first).
            </p>
          </div>
        )}
      </section>
    </>
  );

  const renderAutomations = () => (
    <>
      <section className="pg-section tinted">
        <h2>Automations</h2>
        {!automationsAvailable && (
          <div className="pg-notice warning" style={{ marginTop: 12, marginBottom: 12 }}>
            Automation toggles are not persisted in the backend yet. Rules below reflect platform lifecycle mapping only.
          </div>
        )}
        <div className="pg-grid-2" style={{ marginTop: 12 }}>
          {[
            ['Auto Status Sync', 'Automatically update letter status from PostGrid webhooks.'],
            ['Credit Settlement', 'Settle, release, or refund credits based on final provider status.'],
            ['Failure Recovery', 'Create retry tasks when mail fails or webhook data is incomplete.'],
            ['User Notifications', 'Notify users when letters are sent, delivered, failed, or cancelled.'],
          ].map(([title, desc]) => (
            <div key={title} className="pg-card" style={{ padding: 16 }}>
              <strong>{title}</strong>
              <p className="pg-subtitle" style={{ fontSize: '0.85rem' }}>{desc}</p>
              <p className="pg-subtitle" style={{ fontSize: '0.75rem', marginTop: 8 }}>
                {automationsAvailable ? 'Connected to platform settings' : 'Unavailable — backend setting not configured'}
              </p>
            </div>
          ))}
        </div>
      </section>
      <section className="pg-section">
        <h2>Automation Rules</h2>
        <div className="pg-table-wrap" style={{ marginTop: 12 }}>
          <table className="pg-table">
            <thead>
              <tr>
                <th>Trigger</th>
                <th>Action</th>
                <th>Credit Behavior</th>
                <th>Notification</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {AUTOMATION_RULES.map((rule) => (
                <tr key={rule.trigger}>
                  <td>{rule.trigger}</td>
                  <td>{rule.action}</td>
                  <td>{rule.credit}</td>
                  <td>{rule.notification}</td>
                  <td>
                    <Pill tone={rule.status === 'active' ? 'green' : 'gold'}>{rule.status}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );

  const renderAnalytics = () => (
    <>
      <section className="pg-section tinted">
        <div className="pg-grid-3">
          <div className="pg-metric">
            <div className="pg-label">Delivery Rate</div>
            <div className="pg-value">{analytics.deliveryRate}</div>
            <p className="pg-subtitle" style={{ fontSize: '0.75rem' }}>{metrics.completed ? 'From loaded mail activity' : 'No completed mail yet'}</p>
          </div>
          <div className="pg-metric">
            <div className="pg-label">Avg Cost</div>
            <div className="pg-value">{analytics.avgCost}</div>
            <p className="pg-subtitle" style={{ fontSize: '0.75rem' }}>{metrics.totalCost > 0 ? 'Based on provider cost fields' : 'No provider cost yet'}</p>
          </div>
          <div className="pg-metric">
            <div className="pg-label">Failure Rate</div>
            <div className="pg-value">{analytics.failureRate}</div>
            <p className="pg-subtitle" style={{ fontSize: '0.75rem' }}>{metrics.failed ? 'Includes failed statuses' : 'No failures'}</p>
          </div>
        </div>
      </section>
      <div className="pg-grid-2">
        <section className="pg-section">
          <h2>Mail Volume Trend</h2>
          <div className="pg-chart">
            {Object.keys(analytics.volumeByDay).length === 0 ? (
              <p className="pg-subtitle">No mail volume data in the current page.</p>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 180 }}>
                {Object.entries(analytics.volumeByDay).map(([day, count]) => {
                  const max = Math.max(...Object.values(analytics.volumeByDay), 1);
                  return (
                    <div key={day} style={{ flex: 1, textAlign: 'center' }}>
                      <div
                        style={{
                          height: `${Math.max(8, (count / max) * 160)}px`,
                          background: 'var(--pg-emerald)',
                          borderRadius: '8px 8px 0 0',
                          opacity: 0.85,
                        }}
                      />
                      <small style={{ color: 'var(--pg-muted)', fontSize: '0.7rem' }}>{day}</small>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        <section className="pg-section">
          <h2>Top Agencies by Mail Spend</h2>
          {analytics.topAgencies.length === 0 ? (
            <p className="pg-subtitle">No agency spend data in loaded activity.</p>
          ) : (
            analytics.topAgencies.map(([name, spend]) => (
              <div key={name} className="pg-hbar">
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                <div className="pg-hbar-track">
                  <div
                    className="pg-hbar-fill"
                    style={{ width: `${analytics.maxAgency ? (spend / analytics.maxAgency) * 100 : 0}%` }}
                  />
                </div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(spend)}</div>
              </div>
            ))
          )}
        </section>
      </div>
    </>
  );

  const renderUsage = () => (
    <>
      <section className="pg-section tinted">
        <div className="pg-metric-grid">
          {[
            ['Monthly Mail', metrics.total, 'Loaded records'],
            ['Mail Credits Used', metrics.usedCredits, 'From activity'],
            ['Provider Cost', formatMoney(metrics.totalCost), 'PostGrid spend'],
            ['Certified Mail', metrics.certified, 'Certified class'],
            ['ERR Mail', metrics.errMail, 'ERR / return receipt'],
            ['Refunded Credits', metrics.refundedCredits, 'Released/refunded'],
          ].map(([title, val, help]) => (
            <div key={title} className="pg-metric">
              <div className="pg-label">{title}</div>
              <div className="pg-value" style={{ fontSize: '1.1rem' }}>{val}</div>
              <p className="pg-subtitle" style={{ fontSize: '0.75rem' }}>{help}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="pg-section">
        <h2>Usage by Mail Class</h2>
        <div className="pg-table-wrap" style={{ marginTop: 12 }}>
          <table className="pg-table">
            <thead>
              <tr>
                <th>Mail Class</th>
                <th>Volume</th>
                <th>Provider Cost</th>
                <th>Credits Used</th>
                <th>Avg Cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {usageByClass.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  <td>{row.volume}</td>
                  <td>{formatMoney(row.cost)}</td>
                  <td>{row.credits}</td>
                  <td>{row.volume ? formatMoney(row.cost / row.volume) : 'N/A'}</td>
                  <td><Pill tone={row.volume ? 'green' : 'default'}>{row.volume ? 'Active' : 'No usage'}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="pg-section">
        <h2>Usage by Agency</h2>
        {usageByAgency.length === 0 ? (
          <div className="pg-empty" style={{ marginTop: 12 }}>
            <p className="pg-subtitle">No agency mail usage yet in loaded activity.</p>
          </div>
        ) : (
          <div className="pg-table-wrap" style={{ marginTop: 12 }}>
            <table className="pg-table">
              <thead>
                <tr>
                  <th>Agency</th>
                  <th>Volume</th>
                  <th>Provider Cost</th>
                </tr>
              </thead>
              <tbody>
                {usageByAgency.map(([name, stats]) => (
                  <tr key={name}>
                    <td>{name}</td>
                    <td>{stats.volume}</td>
                    <td>{formatMoney(stats.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );

  const tabContent = {
    overview: renderOverview,
    'api-settings': renderApiSettings,
    webhooks: renderWebhooks,
    automations: renderAutomations,
    analytics: renderAnalytics,
    'usage-metrics': renderUsage,
  };

  return (
    <div className="postgrid-cc p-4 md:p-6" data-theme={isDark ? 'dark' : 'light'}>
      {fetchFailed && (
        <div className="pg-notice warning mb-4">
          Some PostGrid endpoints could not be reached. Showing available data only.
        </div>
      )}

      <div className="pg-topbar">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Button type="button" variant="ghost" size="icon" onClick={() => navigate('/platform/settings')}>
            <ArrowLeft size={18} />
          </Button>
          <div className="pg-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={22} style={{ color: 'var(--pg-emerald)' }} />
              <h1>PostGrid Control Center</h1>
            </div>
            <p className="pg-subtitle">
              Manage PostGrid integration, sender resolution, webhooks, automations, analytics, and mail usage.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="button" variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            <span className="ml-2">Refresh Status</span>
          </Button>
          <Button type="button" size="sm" onClick={handleTestConnection} disabled={testing}>
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            <span className="ml-2">Run Connection Test</span>
          </Button>
        </div>
      </div>

      <nav className="pg-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              className={`pg-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {tabContent[activeTab]?.()}
    </div>
  );
}
